import { useState } from 'react';
import { MessageSquareWarning } from 'lucide-react';
import { cn, isIosHomeScreenApp, outboundHref, reportPageUrl } from '@higgins/utils';
import { buildInfo } from '../lib/feedback-diagnostics';
import { FeedbackFormView, useFeedbackForm, type FeedbackFormProps } from './feedback-form';
import { Button } from './ui/button';
import { SidePanel } from './side-panel';

export type FeedbackButtonProps = FeedbackFormProps & {
  /** Hide the text label and show only the icon (for tight toolbars). */
  iconOnly?: boolean;
  triggerClassName?: string;
};

const LABEL = 'Report a problem / Suggest';
const TRIGGER_CLASS = 'text-slate-600 hover:text-slate-900';

/**
 * The header's Report a problem / Suggest button (FEEDBACK-PLAN.md §3), just left of HelpPanel.
 * Opens the same side panel Help uses: Problem or Suggestion, one text box, Send.
 * `AppHeader` shows it for an app that passes `feedback`.
 */
export function FeedbackButton({ iconOnly = false, triggerClassName, ...formProps }: FeedbackButtonProps) {
  const [open, setOpen] = useState(false);
  const form = useFeedbackForm(formProps, () => setOpen(false));

  function onOpenChange(nextOpen: boolean) {
    setOpen(nextOpen);
    // A sent or kept report is finished: the next open starts afresh. A refused one keeps its draft.
    if (!nextOpen && form.finished) form.clear();
  }

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      title={LABEL}
      description="Tell the apps team about a problem, or suggest something that would help."
      trigger={
        <Button variant="ghost" size="sm" aria-label={LABEL} title={LABEL} className={cn(TRIGGER_CLASS, triggerClassName)}>
          <TriggerContent iconOnly={iconOnly} />
        </Button>
      }
    >
      <FeedbackFormView form={form} onDone={() => onOpenChange(false)} myReportsLink={formProps.myReportsLink} />
    </SidePanel>
  );
}

function TriggerContent({ iconOnly }: { iconOnly: boolean }) {
  return (
    <>
      <MessageSquareWarning className={cn('h-4 w-4', !iconOnly && 'mr-1.5')} />
      {!iconOnly && LABEL}
    </>
  );
}

/**
 * The same button for an app with no API of its own (Estimate to Jobpac, Power BI Help,
 * FEEDBACK-PLAN.md §3.3 and §11.1): it opens App Feedback's report page in a new tab, with the
 * app's key, the page and the build in the address. App Feedback's API saves the report as the
 * user signed in there and records the app as a label only.
 */
export function FeedbackLinkButton({ appKey, iconOnly = false, triggerClassName }: { appKey: string; iconOnly?: boolean; triggerClassName?: string }) {
  // Built at the press, so the page is the one the user is on now.
  function openReportPage() {
    const url = reportPageUrl(appKey, window.location.pathname, buildInfo());
    // An iPad Home Screen app has no tabs: Edge opens it, where the Entra sign-in lives, and the app stays put (as OutboundLink).
    if (isIosHomeScreenApp()) window.location.href = outboundHref(url, 'edge', true);
    else window.open(url, '_blank', 'noopener,noreferrer');
  }
  return (
    <Button variant="ghost" size="sm" aria-label={LABEL} title={LABEL} className={cn(TRIGGER_CLASS, triggerClassName)} onClick={openReportPage}>
      <TriggerContent iconOnly={iconOnly} />
    </Button>
  );
}
